import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api, type CourseEdition, type EmailTemplate, type MarketingCampaign } from "@/lib/api";

type TemplateDraft = { name: string; subject: string; body_html: string; category: string };
type CampaignDraft = { name: string; subject: string; body_html: string; audience: string; edition_id: string };

const emptyTemplate: TemplateDraft = { name: "", subject: "", body_html: "", category: "anuncio" };
const emptyCampaign: CampaignDraft = { name: "", subject: "", body_html: "", audience: "all_students", edition_id: "" };

const presetTemplates: TemplateDraft[] = [
  {
    name: "Invitación a curso",
    subject: "Nuevo programa disponible en KSM Services",
    category: "anuncio",
    body_html: `<h2 style="color:#38424E;margin:0 0 12px;font-size:24px">Hola {{nombre}}, tenemos una nueva capacitación para ti</h2>
<p style="color:#4b5563;line-height:1.6;margin:0 0 16px">Abrimos una nueva fecha de formación especializada para fortalecer las competencias de seguridad industrial de tu equipo.</p>
<div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin:18px 0">
  <p style="margin:0 0 8px"><strong>Ideal para:</strong> personal operativo, supervisores y responsables HSE.</p>
  <p style="margin:0"><strong>Incluye:</strong> evaluación, constancia/certificado y soporte del equipo KSM.</p>
</div>
<p style="margin:24px 0"><a href="https://ksmservices.tech/registro" style="background:#D4A03C;color:#111827;padding:13px 22px;border-radius:10px;text-decoration:none;font-weight:bold;display:inline-block">Solicitar inscripción</a></p>
<p style="color:#6b7280;font-size:13px;line-height:1.5">Si deseas una fecha para tu empresa, responde este correo y te ayudamos a coordinarla.</p>`,
  },
  {
    name: "Recordatorio de pago",
    subject: "Recordatorio de pago pendiente - KSM Services",
    category: "pago",
    body_html: `<h2 style="color:#38424E;margin:0 0 12px;font-size:22px">Hola {{nombre}}, tienes un pago pendiente</h2>
<p style="color:#4b5563;line-height:1.6;margin:0 0 16px">Te recordamos completar el pago de tu inscripción para asegurar tu lugar y evitar retrasos en la emisión de documentos.</p>
<div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:12px;padding:16px;margin:18px 0;color:#7c2d12">
  <strong>Importante:</strong> conserva tu comprobante y súbelo desde tu portal de estudiante.
</div>
<p style="margin:24px 0"><a href="https://ksmservices.tech/login" style="background:#38424E;color:#ffffff;padding:13px 22px;border-radius:10px;text-decoration:none;font-weight:bold;display:inline-block">Ir al portal</a></p>
<p style="color:#6b7280;font-size:13px;line-height:1.5">Si ya realizaste el pago, puedes ignorar este mensaje.</p>`,
  },
  {
    name: "Mensaje informativo",
    subject: "Información importante de KSM Services",
    category: "general",
    body_html: `<h2 style="color:#38424E;margin:0 0 12px;font-size:22px">Hola {{nombre}}</h2>
<p style="color:#4b5563;line-height:1.6;margin:0 0 16px">Queremos compartirte información importante sobre nuestros programas de capacitación y certificación.</p>
<ul style="color:#4b5563;line-height:1.7;margin:0 0 18px;padding-left:20px">
  <li>Capacitaciones para personal operativo y administrativo.</li>
  <li>Programas presenciales, corporativos y bajo solicitud.</li>
  <li>Certificados digitales verificables.</li>
</ul>
<p style="margin:24px 0"><a href="https://ksmservices.tech/#contacto" style="background:#D4A03C;color:#111827;padding:13px 22px;border-radius:10px;text-decoration:none;font-weight:bold;display:inline-block">Contactar a KSM</a></p>`,
  },
];

const htmlBlocks = [
  {
    label: "Título",
    html: `<h2 style="color:#38424E;margin:0 0 12px;font-size:22px">Título del mensaje</h2>`,
  },
  {
    label: "Párrafo",
    html: `<p style="color:#4b5563;line-height:1.6;margin:0 0 16px">Escribe aquí el mensaje principal.</p>`,
  },
  {
    label: "Botón",
    html: `<p style="margin:24px 0"><a href="https://ksmservices.tech/registro" style="background:#D4A03C;color:#111827;padding:13px 22px;border-radius:10px;text-decoration:none;font-weight:bold;display:inline-block">Ver información</a></p>`,
  },
  {
    label: "Aviso",
    html: `<div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin:18px 0;color:#374151"><strong>Nota:</strong> agrega aquí una indicación importante.</div>`,
  },
  {
    label: "Lista",
    html: `<ul style="color:#4b5563;line-height:1.7;margin:0 0 18px;padding-left:20px"><li>Punto importante</li><li>Segundo punto</li><li>Tercer punto</li></ul>`,
  },
];

function emailPreviewHtml(bodyHtml: string) {
  const body = bodyHtml.replace(/\{\{nombre\}\}/g, "María López");
  return `<!doctype html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width,initial-scale=1" /></head><body style="margin:0;background:#eef2f7;padding:24px;font-family:Arial,sans-serif">
    <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto;background:#fff;padding:32px;border:1px solid #e5e5e5;border-radius:14px">
      <div style="text-align:center;margin-bottom:20px"><strong style="color:#38424E;font-size:18px">KSM Services</strong></div>
      ${body || `<p style="color:#6b7280">La vista previa aparecerá aquí cuando escribas el contenido.</p>`}
      <p style="color:#999;font-size:11px;text-align:center;margin-top:28px">KSM Services - Capacitación Industrial Certificada</p>
    </div>
  </body></html>`;
}

function EmailPreview({ subject, bodyHtml }: { subject: string; bodyHtml: string }) {
  const srcDoc = useMemo(() => emailPreviewHtml(bodyHtml), [bodyHtml]);
  return (
    <div className="card p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)]">
        <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Vista previa</p>
        <p className="text-sm font-semibold text-[var(--color-text-primary)] truncate">{subject || "Asunto del correo"}</p>
        <p className="text-xs text-[var(--color-text-muted)] mt-1">Ejemplo usando nombre: María López</p>
      </div>
      <iframe title="Vista previa del correo" srcDoc={srcDoc} sandbox="" className="w-full h-[520px] bg-white" />
    </div>
  );
}

export default function MarketingPage() {
  const [tab, setTab] = useState<"templates" | "campaigns" | "logs">("templates");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [logs, setLogs] = useState<{ id: string; to_email: string; subject: string; type: string; status: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<TemplateDraft>(emptyTemplate);
  const [campaignForm, setCampaignForm] = useState<CampaignDraft>(emptyCampaign);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [sendAudience, setSendAudience] = useState<Record<string, { audience: string; edition_id?: string }>>({});
  const [sending, setSending] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<TemplateDraft>(emptyTemplate);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, l, ed] = await Promise.all([api.getTemplates(), api.getCampaigns(), api.getEmailLogs(), api.getEditions()]);
      setTemplates(t);
      setCampaigns(c);
      setLogs(l);
      setEditions(ed.filter((e) => e.status !== "cancelado"));
    } catch {
      /* fallback silencioso */
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const appendToTemplate = (html: string) => setForm((f) => ({ ...f, body_html: `${f.body_html}${f.body_html ? "\n" : ""}${html}` }));
  const appendToCampaign = (html: string) => setCampaignForm((f) => ({ ...f, body_html: `${f.body_html}${f.body_html ? "\n" : ""}${html}` }));

  const handleCreateTemplate = async (e: FormEvent) => {
    e.preventDefault();
    await api.createTemplate(form);
    setForm(emptyTemplate);
    load();
  };

  const handleCreateCampaign = async (e: FormEvent) => {
    e.preventDefault();
    if (campaignForm.audience === "course" && !campaignForm.edition_id) {
      alert("Seleccione una edición para la audiencia");
      return;
    }
    await api.createCampaign(campaignForm);
    setCampaignForm(emptyCampaign);
    load();
  };

  const handleSend = async (id: string) => {
    const opts = sendAudience[id] || { audience: "all_students" };
    if (opts.audience === "course" && !opts.edition_id) {
      alert("Seleccione una edición");
      return;
    }
    setSending(id);
    try {
      const result = await api.sendCampaign(id, opts);
      alert(`Enviados: ${result.sent} de ${result.total}`);
      load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al enviar");
    }
    setSending(null);
  };

  const handleTestEmail = async (t: Pick<EmailTemplate, "subject" | "body_html">) => {
    const email = window.prompt("Introduce el correo destino para la prueba:");
    if (!email) return;
    try {
      await api.sendTestEmail({ to: email, subject: t.subject, body_html: t.body_html });
      alert("Correo de prueba enviado con éxito.");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al enviar prueba");
    }
  };

  const handleUseTemplate = (t: EmailTemplate) => {
    setCampaignForm((f) => ({ ...f, subject: t.subject, body_html: t.body_html }));
    setTab("campaigns");
  };

  const startEditTemplate = (t: EmailTemplate) => {
    setEditingId(t.id);
    setEditForm({ name: t.name, subject: t.subject, body_html: t.body_html, category: t.category });
  };

  const handleUpdateTemplate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    await api.updateTemplate(editingId, editForm);
    setEditingId(null);
    load();
  };

  return (
    <div className="space-y-6 animate-[fadeIn_0.4s_ease-out]">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Marketing & Email</h1>
        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
          Crea correos con plantillas, bloques HTML listos, vista previa y envío de prueba antes de lanzar campañas.
        </p>
      </div>

      <div className="card bg-[var(--color-bg-card)]/70">
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-semibold text-[var(--color-text-primary)] mb-1">1. Escribe simple</p>
            <p className="text-[var(--color-text-muted)]">Usa los bloques de título, párrafo, botón y aviso. Ya traen estilos compatibles con correo.</p>
          </div>
          <div>
            <p className="font-semibold text-[var(--color-text-primary)] mb-1">2. Personaliza</p>
            <p className="text-[var(--color-text-muted)]">Escribe <code className="px-1 rounded bg-[var(--color-bg-secondary)]">{"{{nombre}}"}</code> donde quieras mostrar el nombre del estudiante.</p>
          </div>
          <div>
            <p className="font-semibold text-[var(--color-text-primary)] mb-1">3. Prueba antes</p>
            <p className="text-[var(--color-text-muted)]">Revisa la vista previa y envía una prueba a tu correo antes de mandar la campaña.</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[var(--color-border)] pb-2 overflow-x-auto">
        {(["templates", "campaigns", "logs"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              tab === t ? "bg-[var(--color-accent-muted)] text-[var(--color-text-accent)]" : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-card)]"
            }`}>
            {t === "templates" ? "Plantillas" : t === "campaigns" ? "Campañas" : "Historial"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--color-text-muted)]"><i className="ri-loader-4-line animate-spin text-2xl"></i></div>
      ) : tab === "templates" ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
          <div className="space-y-6">
            <form onSubmit={handleCreateTemplate} className="card space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-semibold text-[var(--color-text-primary)]">Nueva plantilla</h2>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">Guarda una base reutilizable para futuras campañas.</p>
                </div>
                <button type="button" onClick={() => handleTestEmail(form)} disabled={!form.subject || !form.body_html} className="btn-secondary text-xs disabled:opacity-50">
                  <i className="ri-mail-send-line"></i> Enviar prueba
                </button>
              </div>

              <div className="grid md:grid-cols-3 gap-3">
                {presetTemplates.map((preset) => (
                  <button key={preset.name} type="button" onClick={() => setForm(preset)} className="text-left rounded-xl border border-[var(--color-border)] p-3 hover:border-[var(--color-accent)]/50 hover:bg-[var(--color-bg-card-hover)] transition-colors">
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">{preset.name}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">{preset.subject}</p>
                  </button>
                ))}
              </div>

              <input className="input-field" placeholder="Nombre interno de la plantilla" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input className="input-field" placeholder="Asunto que verá el destinatario" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required />
              <select className="input-field" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="anuncio">Anuncio de curso</option>
                <option value="pago">Recordatorio de pago</option>
                <option value="general">General</option>
              </select>

              <div>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Contenido HTML</label>
                  <div className="flex gap-1 flex-wrap">
                    {htmlBlocks.map((block) => (
                      <button key={block.label} type="button" onClick={() => appendToTemplate(block.html)} className="px-2 py-1 rounded-lg border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-accent)] hover:border-[var(--color-accent)]/50">
                        + {block.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea className="input-field min-h-[260px] font-mono text-xs" placeholder="Usa los botones de bloques o pega HTML sencillo. Ejemplo: <p>Hola {{nombre}}</p>"
                  value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} required />
              </div>

              <button type="submit" className="btn-primary w-full">Guardar plantilla</button>
            </form>

            <div className="space-y-3">
              <h2 className="font-semibold text-[var(--color-text-primary)]">Plantillas guardadas</h2>
              {templates.map((t) => (
                <div key={t.id} className="card flex items-start justify-between gap-4">
                  {editingId === t.id ? (
                    <form onSubmit={handleUpdateTemplate} className="flex-1 space-y-3">
                      <input className="input-field" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required />
                      <input className="input-field" value={editForm.subject} onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })} required />
                      <select className="input-field" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}>
                        <option value="anuncio">Anuncio de curso</option>
                        <option value="pago">Recordatorio de pago</option>
                        <option value="general">General</option>
                      </select>
                      <textarea className="input-field min-h-[160px] font-mono text-xs" value={editForm.body_html} onChange={(e) => setEditForm({ ...editForm, body_html: e.target.value })} required />
                      <div className="flex gap-2 flex-wrap">
                        <button type="submit" className="btn-primary text-xs">Guardar</button>
                        <button type="button" onClick={() => setEditingId(null)} className="btn-secondary text-xs">Cancelar</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div>
                        <p className="font-semibold text-[var(--color-text-primary)]">{t.name}</p>
                        <p className="text-xs text-[var(--color-text-muted)]">{t.subject}</p>
                        <span className="badge-info text-[10px] mt-1 inline-block">{t.category}</span>
                      </div>
                      <div className="flex flex-col gap-1">
                        <button onClick={() => handleUseTemplate(t)} className="btn-secondary text-xs whitespace-nowrap cursor-pointer">Usar en campaña</button>
                        <button onClick={() => handleTestEmail(t)} className="btn-secondary text-xs whitespace-nowrap cursor-pointer"><i className="ri-mail-send-line"></i> Enviar prueba</button>
                        <button onClick={() => setForm({ name: `${t.name} copia`, subject: t.subject, body_html: t.body_html, category: t.category })} className="text-xs text-[var(--color-accent)] hover:underline cursor-pointer">Copiar al editor</button>
                        <button onClick={() => startEditTemplate(t)} className="text-xs text-[var(--color-accent)] hover:underline cursor-pointer">Editar</button>
                        <button onClick={async () => { if (confirm("¿Eliminar plantilla?")) { await api.deleteTemplate(t.id); load(); } }} className="text-xs text-red-400 hover:underline cursor-pointer">Eliminar</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {templates.length === 0 && <p className="text-sm text-[var(--color-text-muted)]">Sin plantillas aún. Usa una plantilla rápida para empezar.</p>}
            </div>
          </div>
          <EmailPreview subject={form.subject} bodyHtml={form.body_html} />
        </div>
      ) : tab === "campaigns" ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
          <div className="space-y-6">
            <form onSubmit={handleCreateCampaign} className="card space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-semibold text-[var(--color-text-primary)]">Nueva campaña</h2>
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">Crea el correo y decide la audiencia al enviarlo.</p>
                </div>
                <button type="button" onClick={() => handleTestEmail({ subject: campaignForm.subject, body_html: campaignForm.body_html })} disabled={!campaignForm.subject || !campaignForm.body_html} className="btn-secondary text-xs disabled:opacity-50">
                  <i className="ri-mail-send-line"></i> Enviar prueba
                </button>
              </div>

              {templates.length > 0 && (
                <select className="input-field" value="" onChange={(e) => {
                  const selected = templates.find((t) => t.id === e.target.value);
                  if (selected) handleUseTemplate(selected);
                }}>
                  <option value="">Usar plantilla guardada...</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name} - {t.subject}</option>)}
                </select>
              )}

              <input className="input-field" placeholder="Nombre interno de campaña" value={campaignForm.name} onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })} required />
              <input className="input-field" placeholder="Asunto del email" value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} required />
              <select className="input-field" value={campaignForm.audience} onChange={(e) => setCampaignForm({ ...campaignForm, audience: e.target.value, edition_id: "" })}>
                <option value="all_students">Todos los estudiantes</option>
                <option value="enrolled">Inscritos activos (todas las ediciones)</option>
                <option value="course">Estudiantes de una edición</option>
              </select>
              {campaignForm.audience === "course" && (
                <select className="input-field" value={campaignForm.edition_id} onChange={(e) => setCampaignForm({ ...campaignForm, edition_id: e.target.value })} required>
                  <option value="">Seleccione edición</option>
                  {editions.map((ed) => (
                    <option key={ed.id} value={ed.id}>{ed.course?.name || "Curso/Diplomado"} - {ed.edition_label || ed.start_date.slice(0, 10)}</option>
                  ))}
                </select>
              )}

              <div>
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Contenido HTML</label>
                  <div className="flex gap-1 flex-wrap">
                    {htmlBlocks.map((block) => (
                      <button key={block.label} type="button" onClick={() => appendToCampaign(block.html)} className="px-2 py-1 rounded-lg border border-[var(--color-border)] text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-accent)] hover:border-[var(--color-accent)]/50">
                        + {block.label}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea className="input-field min-h-[260px] font-mono text-xs" placeholder="Contenido HTML del correo. Usa {{nombre}} para personalizar."
                  value={campaignForm.body_html} onChange={(e) => setCampaignForm({ ...campaignForm, body_html: e.target.value })} required />
              </div>
              <button type="submit" className="btn-primary w-full">Crear campaña</button>
            </form>

            <div className="space-y-3">
              <h2 className="font-semibold text-[var(--color-text-primary)]">Campañas</h2>
              {campaigns.map((c) => (
                <div key={c.id} className="card">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-[var(--color-text-primary)]">{c.name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{c.subject}</p>
                      <span className={`text-[10px] mt-1 inline-block ${c.status === "enviada" ? "badge-success" : "badge-warning"}`}>
                        {c.status} {c.sent_count > 0 ? `(${c.sent_count} enviados)` : ""}
                      </span>
                    </div>
                    {c.status !== "enviada" && (
                      <div className="flex flex-col gap-2 items-end">
                        <select className="input-field text-xs py-1" value={sendAudience[c.id]?.audience || "all_students"} onChange={(e) => setSendAudience((s) => ({ ...s, [c.id]: { audience: e.target.value, edition_id: "" } }))}>
                          <option value="all_students">Todos</option>
                          <option value="enrolled">Inscritos</option>
                          <option value="course">Por edición</option>
                        </select>
                        {(sendAudience[c.id]?.audience || "all_students") === "course" && (
                          <select className="input-field text-xs py-1" value={sendAudience[c.id]?.edition_id || ""} onChange={(e) => setSendAudience((s) => ({ ...s, [c.id]: { audience: "course", edition_id: e.target.value } }))}>
                            <option value="">Edición</option>
                            {editions.map((ed) => <option key={ed.id} value={ed.id}>{ed.course?.name} - {ed.edition_label || ed.start_date.slice(0, 10)}</option>)}
                          </select>
                        )}
                        <button disabled={sending === c.id} onClick={() => handleSend(c.id)} className="btn-primary text-xs whitespace-nowrap cursor-pointer disabled:opacity-50">
                          {sending === c.id ? "Enviando..." : "Enviar ahora"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {campaigns.length === 0 && <p className="text-sm text-[var(--color-text-muted)]">Sin campañas aún.</p>}
            </div>
          </div>
          <EmailPreview subject={campaignForm.subject} bodyHtml={campaignForm.body_html} />
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="pb-3 pr-4">Fecha</th>
                <th className="pb-3 pr-4">Destino</th>
                <th className="pb-3 pr-4">Asunto</th>
                <th className="pb-3 pr-4">Tipo</th>
                <th className="pb-3">Estado</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-[var(--color-border)]/50">
                  <td className="py-3 pr-4 text-xs text-[var(--color-text-muted)]">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="py-3 pr-4">{l.to_email}</td>
                  <td className="py-3 pr-4">{l.subject}</td>
                  <td className="py-3 pr-4">{l.type}</td>
                  <td className="py-3"><span className={l.status === "sent" ? "badge-success" : l.status === "skipped" ? "badge-warning" : "badge-danger"}>{l.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
